import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import Footer from 'components/Footer';

import CollectionItem from 'components/CollectionItem';
import Modal from 'components/Modal';
import Checkbox from 'components/common/Checkbox';
import ClipboardButton from 'react-clipboard.js';

import cn from 'classnames';
import styles from './Collection.scss';
import nothingFound from 'assets/images/nothingfound@2x.png';
import iconLink from 'assets/images/icons/icon-link-dark.svg';
import iconCheck from 'assets/images/icons/check-icon.svg';
import arrow from '../../assets/images/icons/arrow-dropdown.svg';
import check from '../../assets/images/icons/check-green.svg';

import { loadCollection, switchInfoAccess } from 'actions/collections';
import { setPopUp } from 'actions/environment';
import tracker from '../../services/tracker';
import { useTranslation, withTranslation } from 'react-i18next';
import { getLotsValues, makeBase64, firstLetterToUpper } from '../Building/Main/helper';
import { Select } from '../Login/FormRegistration/Select';
import { gaEvent, trackGA } from 'services/ga';
import i18n from '../../i18n';

const StatsInfoField = ({ title, text }) => (
  <div className={styles.StatsInfoField}>
    <div className={styles.StatsInfoFieldTitle}>{title}</div>
    <div className={styles.SendCollectionBtnText}>{text}</div>
  </div>
);

const formatTime = (seconds, t) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} ${t('components.collection.hour')}`);
  if (minutes > 0) parts.push(`${minutes} ${t('components.collection.min')}`);
  if (remainingSeconds > 0) parts.push(`${remainingSeconds} ${t('components.collection.sec')}`);

  return parts.join(' ');
};

const formatDate = (dateStr, t) => {
  const date = new Date(dateStr);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const day = date.getDate();
  const month = t(`utils.monthToString.${date.getMonth()}`);
  const year = date.getFullYear();

  return {
    formattedTime: `${hours}:${minutes}`,
    formattedDate: `${day} ${month} ${year}`,
    date,
  };
};

const formatVisitDates = (start, end, t) => {
  const startFormatted = formatDate(start, t);
  const endFormatted = formatDate(end, t);

  if (!startFormatted || !endFormatted) {
    return '';
  }

  if (startFormatted.date.toDateString() === endFormatted.date.toDateString()) {
    return `${startFormatted.formattedTime} – ${endFormatted.formattedTime}, ${startFormatted.formattedDate}`;
  }
  return `${startFormatted.formattedTime}, ${startFormatted.formattedDate} – ${endFormatted.formattedTime}, ${endFormatted.formattedDate}`;
};

const Collection = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const lotsValues = getLotsValues(t);
  const [values, setValues] = useState({
    nameAddress: true,
    description: true,
    characteristics: true,
    communications: true,
    checkout: true,
    payment: true,
    map: true,
    lots: lotsValues[0],
  });

  const [isOpen, setIsOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    const newLotsValues = getLotsValues(t);
    setValues((prev) => ({
      ...prev,
      lots: newLotsValues[0],
    }));
  }, [i18n.language]);

  const sessionItems =
    props && props.selection && props.selection.sessions && props.selection.sessions.items
      ? props.selection.sessions.items
      : [];

  const sessionTotal =
    props && props.selection && props.selection.sessions && props.selection.sessions.total
      ? props.selection.sessions.total
      : {};

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (value) => {
    setIsOpen(false);
    setSelectedSession(value);
  };

  const dropdownOptions = [
    { label: t('components.collection.allSessions'), value: 'total' },
    ...sessionItems.map((item, index) => ({
      label: `${t('components.collection.session')} ${index + 1}`,
      value: item.session_uuid,
    })),
  ];

  const getCurrentSessionStats = () => {
    if (!selectedSession || selectedSession === 'total') {
      return sessionTotal;
    }

    return sessionItems.find((item) => item.session_uuid === selectedSession);
  };

  const handleTrackEvent = ({ eventName, eventLabel, eventContent, eventContext }) => {
    const { profile } = props;
    trackGA(
      gaEvent[eventName]({
        eventLabel,
        eventContent,
        eventContext,
        user_id: profile.id,
      })
    );
  };

  const handleChange = (name, value) => {
    if (name === 'lots') {
      const event =
        value === t('components.collection.showAllLots')
          ? 'Web_ShareCollectionDialog_Lots_0'
          : value === t('components.collection.onlySelectedLots')
          ? 'Web_ShareCollectionDialog_Lots_1'
          : 'Web_ShareCollectionDialog_Lots_2';

      tracker.sendEvent(event);
    } else {
      tracker.sendEvent(`Web_ShareCollectionDialog_${firstLetterToUpper(name)}`, {
        selectedSession: value,
      });
    }

    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    dispatch(loadCollection({ referal: props.match.params.referal }));

    trackGA(
      gaEvent.pageShow({
        screenName: location.pathname,
      })
    );
  }, []);

  const handleClipboardClick = () => {
    tracker.sendEvent('Web_ShareCollectionDialog_CopyButton');

    handleTrackEvent({
      eventName: 'collectionShareDialogButtonsClick',
      eventLabel: 'skopirovat',
      eventContext: props.selection.name,
    });

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 5000);
  };

  const handleClose = () => {
    handleTrackEvent({
      eventName: 'collectionShareDialogButtonsClick',
      eventLabel: 'zakryt',
      eventContext: props.selection.name,
    });

    setShowModal(false);
  };

  const showLinkModal = () => {
    tracker.sendEvent('Web_Selections_Share');

    handleTrackEvent({
      eventName: 'collectionShareButtonClick',
      eventContext: props.selection.name,
    });

    setShowModal(true);
  };

  const handleInputFocus = (e) => {
    e.target.selectionStart = 0;
    e.target.selectionEnd = e.target.value.length;
    tracker.sendEvent('Web_ShareCollectionDialog_Link');
  };

  const [shareUrl, setShareUrl] = useState('');
  useEffect(() => {
    makeBase64({
      r: props.match.params.referal,
      values,
    }).then((hash) => setShareUrl(`${location.origin}/ncollection/${hash}`));
  }, [values]);

  const {
    className,
    buildings,
    selection: { name },
  } = props;

  useEffect(() => {
    if (name && !buildings.length) {
      handleTrackEvent({
        eventName: 'collectionEmptyCardShow',
        eventContext: props.selection.name,
      });
    }
  }, [buildings]);

  const currentStats = getCurrentSessionStats();

  return (
    <React.Fragment>
      <Helmet>
        <title>{t('components.collection.title', { name })}</title>
      </Helmet>
      <div className={styles.CollectionWrap}>
        <div className={cn(styles.Collection, className)}>
          <div className={styles.Container}>
            <div className={styles.TitleBlock}>
              <div className={styles.TitleBlockLeft}>
                <p className={styles.Title}>
                  <Link to="/collections">{t('components.collection.myCollections')}</Link> / {name}
                </p>
                {buildings && (
                  <span className={styles.ObjectsCount}>
                    {buildings.length || 0} {t('common.jk')}
                  </span>
                )}
              </div>
              {buildings && (
                <div
                  onClick={showLinkModal}
                  className={cn(styles.SendCollectionBtn, 'button-primary', 'button-primary--lg')}
                >
                  {t('components.collection.sendCollection')}
                </div>
              )}
              <Modal
                handleClose={handleClose}
                isOpen={showModal}
                title={t('components.collection.sendCollection')}
                contentStyle={{
                  width: '583px',
                  margin: 'auto',
                }}
              >
                <div className={styles.Modal}>
                  <Select
                    style={{ marginBottom: 16, width: 280 }}
                    id="lots"
                    inputStyle={{ fontSize: 15 }}
                    placeholder="—"
                    value={values.lots}
                    options={lotsValues}
                    onChange={(value) => handleChange('lots', value)}
                    onClick={() =>
                      handleTrackEvent({
                        eventName: 'collectionShareDialogDropdownClick',
                        eventContext: props.selection.name,
                      })
                    }
                  />
                  <p className={styles.ModalDescription}>Какие разделы будут видны по ссылке</p>
                  <div className={styles.ModalCheckboxes}>
                    <Checkbox
                      checked={values.nameAddress}
                      handle={(value) => {
                        handleChange('nameAddress', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Название и адрес', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.nameAndAddress')}
                    />
                    <Checkbox
                      checked={values.description}
                      handle={(value) => {
                        handleChange('description', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Описание и преимущества', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.descriptionAndAdvantages')}
                    />
                    <Checkbox
                      checked={values.characteristics}
                      handle={(value) => {
                        handleChange('characteristics', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Общие характеристики', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.mainInfo')}
                    />
                    <Checkbox
                      checked={values.communications}
                      handle={(value) => {
                        handleChange('communications', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Коммуникации', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.communications')}
                    />
                    <Checkbox
                      checked={values.checkout}
                      handle={(value) => {
                        handleChange('checkout', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Оформление', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.documents')}
                    />
                    <Checkbox
                      checked={values.payment}
                      handle={(value) => {
                        handleChange('payment', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Оплата', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.payments')}
                    />
                    <Checkbox
                      checked={values.map}
                      handle={(value) => {
                        handleChange('map', value);
                        handleTrackEvent({
                          eventName: 'collectionShareDialogCheckboxesPick',
                          eventContent: ['Карта', ` | ${value ? 'on' : 'off'}`],
                          eventContext: props.selection.name,
                        });
                      }}
                      text={t('components.collection.map')}
                    />
                  </div>
                  <p className={styles.ModalDescription}>{t('components.collection.sendLink')}</p>
                  <div className={styles.SendCollectionInputWrap}>
                    <img src={iconLink} alt="" />
                    <input
                      readOnly
                      value={shareUrl}
                      className={styles.SendCollectionInput}
                      onFocus={handleInputFocus}
                    />
                  </div>
                  <ClipboardButton
                    className={cn(styles.SendCollectionBtn, 'button-primary', 'button-primary--sm')}
                    data-clipboard-text={shareUrl}
                    onClick={handleClipboardClick}
                  >
                    {t('components.collection.copy')}
                  </ClipboardButton>
                  {showNotification && (
                    <span className={styles.ModalNotification}>
                      <img src={iconCheck} alt={t('components.collection.copied')} />{' '}
                      {t('components.collection.copied')}
                    </span>
                  )}
                </div>
              </Modal>
            </div>
            <div className={styles.CollectionItemsContainer}>
              <div className={styles.StatsContainer}>
                <div className={styles.StatsSessions} onClick={toggleDropdown} ref={dropdownRef}>
                  <span className={styles.SelectedSession}>
                    {(() => {
                      const found = dropdownOptions.find((opt) => opt.value === selectedSession);
                      return found ? found.label : t('components.collection.allSessions');
                    })()}
                    <img src={arrow} alt="arrow" className={!isOpen && styles.ArrowDown} />
                  </span>

                  {isOpen && (
                    <ul
                      className={`${styles.Options} ${
                        dropdownOptions.length > 10 ? styles.Scroll : ''
                      }`}
                    >
                      {dropdownOptions.map((option) => (
                        <li
                          key={option.value}
                          className={styles.Option}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSelect(option.value);
                          }}
                        >
                          <div>{option.label}</div>
                          {option.value === selectedSession && <img src={check} alt="check" />}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={styles.StatsCard}>
                  <StatsInfoField
                    title={t('components.collection.viewsCount')}
                    text={currentStats.total_views || '-'}
                  />
                  <StatsInfoField
                    title={t('components.collection.timeSpent')}
                    text={
                      currentStats.total_time_spent
                        ? formatTime(currentStats.total_time_spent, t)
                        : '-'
                    }
                  />
                  <StatsInfoField
                    title={t('components.collection.lastVisitDate')}
                    text={
                      currentStats.last_visit_start && currentStats.last_visit_end
                        ? formatVisitDates(
                            currentStats.last_visit_start,
                            currentStats.last_visit_end,
                            t
                          )
                        : '-'
                    }
                  />
                </div>
              </div>
              {buildings && buildings.length ? (
                buildings.length ? (
                  <div className={styles.CollectionItems}>
                    {buildings &&
                      buildings.map((building, i) => (
                        <CollectionItem
                          building={building}
                          key={building.id + (building.comment || i)}
                          sessionId={selectedSession === 'total' ? null : selectedSession}
                        />
                      ))}
                  </div>
                ) : null
              ) : (
                <div className={styles.NotFoundBlock}>
                  <div className={styles.NotFoundMain}>
                    <p className={styles.NotFoundTitle}>
                      {t('components.collection.emptyCollection')}
                    </p>
                    <p className={styles.NotFoundText}>{t('components.collection.description')}</p>
                    <Link
                      to="/"
                      className={cn(styles.NotFoundBtn, 'button-primary', 'button-primary--md')}
                      onClick={() =>
                        handleTrackEvent({
                          eventName: 'collectionEmptyCardButtonClick',
                          eventContext: props.selection.name,
                        })
                      }
                    >
                      {t('components.collection.goToList')}
                    </Link>
                  </div>
                  <div className={styles.NotFoundImage}>
                    <img src={nothingFound} alt="nothing found" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
};

export default connect(
  (state) => ({
    ...state.collection,
    collections: state.collections.data,
    profile: state.profile,
  }),
  { loadCollection, switchInfoAccess, setPopUp }
)(withTranslation()(Collection));



// WEBPACK FOOTER //
// ./src/components/Collection/Collection.js